import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  CreateUnitRequest,
  ListResponse,
  UnitQuery,
  UnitResponse,
  UpdateUnitRequest,
} from '@troia-v3/types';
import { unitsApi } from '../services/unitsApi';

const QUERY_KEY = 'units';

/**
 * Hook to fetch units with pagination and filters.
 */
export const useUnits = (params?: UnitQuery) => {
  return useQuery<ListResponse<UnitResponse>>({
    queryKey: [QUERY_KEY, params],
    queryFn: () => unitsApi.list(params),
    staleTime: 30_000,
  });
};

/**
 * Hook to fetch a single unit by ID.
 */
export const useUnit = (id: string | undefined) => {
  return useQuery<UnitResponse>({
    queryKey: [QUERY_KEY, id],
    queryFn: () => unitsApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
};

/**
 * Hook to create a new unit.
 */
export const useCreateUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUnitRequest) => unitsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      // `units-dropdown` é chave IRMÃ (não prefixo) — sem esta linha o Select
      // de Unidade fica com a lista velha até o staleTime de 5min expirar.
      queryClient.invalidateQueries({ queryKey: ['units-dropdown'] });
      toast.success('Unidade criada com sucesso!');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erro ao criar unidade';
      toast.error(message);
    },
  });
};

/**
 * Hook to update a unit (partial).
 */
export const useUpdateUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUnitRequest }) =>
      unitsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      // `units-dropdown` é chave IRMÃ (não prefixo) — sem esta linha o Select
      // de Unidade fica com a lista velha até o staleTime de 5min expirar.
      queryClient.invalidateQueries({ queryKey: ['units-dropdown'] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
      toast.success('Unidade atualizada com sucesso!');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erro ao atualizar unidade';
      toast.error(message);
    },
  });
};

/**
 * Hook to delete a unit.
 */
export const useDeleteUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => unitsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      // `units-dropdown` é chave IRMÃ (não prefixo) — sem esta linha o Select
      // de Unidade fica com a lista velha até o staleTime de 5min expirar.
      queryClient.invalidateQueries({ queryKey: ['units-dropdown'] });
      toast.success('Unidade excluída com sucesso!');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erro ao excluir unidade';
      toast.error(message);
    },
  });
};
